import React from 'react';

const IMG = (path) => `${process.env.PUBLIC_URL}/images/${path}`;

/** Milestones = direct sponsors (IDs). Images from public/images/rewards/ */
const REWARD_TIERS = [
  { ids: 10, label: '10 direct sponsors', title: 'Branded polo', image: 'rewards/i_tshirt.png', accent: 'from-slate-600 to-slate-800' },
  { ids: 25, label: '25 direct sponsors', title: 'Smartwatch', image: 'rewards/i_watch.png', accent: 'from-indigo-600 to-violet-700' },
  { ids: 50, label: '50 direct sponsors', title: 'Mixer grinder set', image: 'rewards/mixer_grinder.png', accent: 'from-cyan-600 to-teal-700' },
  { ids: 100, label: '100 direct sponsors', title: 'Tablet', image: 'rewards/tab.png', accent: 'from-blue-600 to-indigo-700' },
  { ids: 250, label: '250 direct sponsors', title: 'Android smartphone', image: 'rewards/android_phone.png', accent: 'from-emerald-600 to-cyan-700' },
  { ids: 500, label: '500 direct sponsors', title: 'Premium smartphone', image: 'rewards/iphone.png', accent: 'from-violet-600 to-fuchsia-700' },
  { ids: 1000, label: '1,000 direct sponsors', title: '₹2,00,000 cash reward', image: 'rewards/cash.png', accent: 'from-amber-500 to-orange-600', highlight: true }, //from-amber-500 to-orange-600
];

const REWARD_ROW1 = REWARD_TIERS.slice(0, 4);
const REWARD_ROW2 = REWARD_TIERS.slice(4);

function RewardCard({ tier, row2Index }) {
  const isRow2 = row2Index !== undefined;
  /** Row uses `lg:grid-cols-8`; each card is 2 cols (= same width as old 4-col row). Row 2 starts at col 2 so cols 1 & 8 stay empty → centered trio. */
  const lgGrid = 'lg:col-span-2';
  const row2FirstStart = isRow2 && row2Index === 0 ? 'lg:col-start-2' : '';
  const centerLastOnSm =
    isRow2 && row2Index === 2
      ? 'sm:col-span-2 sm:max-w-md sm:w-full sm:justify-self-center lg:col-span-2 lg:col-start-auto lg:max-w-none lg:w-auto lg:justify-self-auto'
      : '';

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white/80 shadow-[0_16px_44px_-20px_rgba(79,70,229,0.12)] ring-1 ring-slate-200/60 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_56px_-18px_rgba(79,70,229,0.22)] ${
        tier.highlight ? 'border-amber-200/90 ring-amber-200/40' : 'border-white/90'
      } ${lgGrid} ${row2FirstStart} ${centerLastOnSm}`}
    >
      <div className={`flex items-center justify-between bg-gradient-to-r px-4 py-3 text-white ${tier.accent}`}>
        <span className="text-xs font-semibold uppercase tracking-wider text-white/90">{tier.label}</span>
        <span className="rounded-lg bg-white/20 px-2 py-0.5 text-xs font-bold tabular-nums">{tier.ids} ID</span>
      </div>
      <div className="relative flex flex-1 flex-col p-4 sm:p-5">
        <div className="relative mx-auto w-full max-w-[220px] flex-1">
          <div
            className={`absolute -inset-1 rounded-xl opacity-0 blur-md transition duration-500 group-hover:opacity-40 bg-gradient-to-br ${tier.accent}`}
            aria-hidden
          />
          <div className="relative overflow-hidden rounded-xl bg-slate-50 ring-1 ring-slate-200/80">
            <img
              src={IMG(tier.image)}
              alt={tier.title}
              className="aspect-square w-full object-contain p-3 transition duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        </div>
        <h3 className="mt-4 text-center text-sm font-semibold text-slate-900 sm:text-base">{tier.title}</h3>
        {tier.highlight && (
          <p className="mt-2 text-center text-xs font-medium text-amber-800/90">Cash reward upon qualification at 1,000 direct sponsors</p>
        )}
      </div>
    </article>
  );
}

function GlassPanel({ children, className = '' }) {
  return (
    <div
      className={`rounded-[1.75rem] border border-white/90 bg-white/70 shadow-[0_25px_70px_-20px_rgba(79,70,229,0.18),0_8px_32px_-12px_rgba(15,23,42,0.08)] backdrop-blur-2xl ring-1 ring-indigo-950/[0.04] ${className}`}
    >
      {children}
    </div>
  );
}

function Rewards() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 via-indigo-50/35 to-cyan-50/40 text-slate-800">
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-white/80 via-transparent to-cyan-50/30" aria-hidden />
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.85]"
        style={{
          background:
            'radial-gradient(ellipse 85% 50% at 50% -10%, rgba(165,180,252,0.35), transparent 50%), radial-gradient(ellipse 45% 38% at 100% 5%, rgba(207,250,254,0.7), transparent 45%), radial-gradient(ellipse 40% 35% at 0% 35%, rgba(199,210,254,0.45), transparent 42%)',
        }}
        aria-hidden
      />
      <div className="pointer-events-none fixed bottom-0 left-1/2 h-[min(55vh,480px)] w-[min(92vw,880px)] -translate-x-1/2 rounded-full bg-gradient-to-t from-cyan-200/25 via-indigo-200/20 to-transparent blur-3xl" aria-hidden />

      <div className="relative z-10">
        {/* Hero */}
        <section className="px-4 pb-12 pt-10 sm:pb-16 sm:pt-14">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-5 flex flex-wrap items-center justify-center gap-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200/80 bg-white/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-700 shadow-md shadow-indigo-200/30 backdrop-blur-md">
                ICONNECT LLP
              </div>
              <img src={IMG('company_logo.png')} alt="" className="h-9 w-auto opacity-90 sm:h-10" loading="lazy" />
            </div>
            <h1
              className="bg-gradient-to-br from-slate-900 via-indigo-800 to-cyan-700 bg-clip-text text-3xl font-semibold leading-tight tracking-tight text-transparent sm:text-4xl lg:text-5xl"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              Leaders rewards
            </h1>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600/90">Direct sponsor</p>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Exclusive recognition for leaders who grow the network with{' '}
              <span className="font-semibold text-slate-800">verified direct sponsors</span>. Each tier unlocks a premium reward — from branded gear to devices and our top cash milestone.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="px-4 pb-12 sm:pb-16">
          <div className="mx-auto max-w-6xl">
            <GlassPanel className="p-6 sm:p-8 lg:p-10">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-indigo-600">Program</p>
                  <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                    How milestones work
                  </h2>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
                    Milestones count your <strong className="font-semibold text-slate-800">direct sponsors</strong> — members who join using your referral link and meet programme eligibility. Rewards are subject to verification and company policy in effect at the time of qualification.
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-3 lg:justify-end">
                  <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white px-5 py-4 text-center shadow-sm">
                    <p className="text-2xl font-bold text-indigo-700">7</p>
                    <p className="text-xs font-medium text-slate-500">Reward tiers</p>
                  </div>
                  <div className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-white px-5 py-4 text-center shadow-sm">
                    <p className="text-2xl font-bold text-cyan-700">₹2L</p>
                    <p className="text-xs font-medium text-slate-500">Top milestone</p>
                  </div>
                </div>
              </div>
            </GlassPanel>
          </div>
        </section>

        {/* Reward grid */}
        <section className="px-4 pb-20 sm:pb-24">
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 text-center sm:mb-12">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-indigo-600/90">Tiers</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                Rewards by direct sponsors
              </h2>
              <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-indigo-500 via-cyan-500 to-violet-500 shadow-[0_4px_20px_rgba(99,102,241,0.25)]" />
            </div>

            <div className="flex flex-col gap-5 lg:gap-6">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-8 lg:gap-6">
                {REWARD_ROW1.map((tier) => (
                  <RewardCard key={tier.ids} tier={tier} />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-8 lg:gap-6">
                {REWARD_ROW2.map((tier, row2Index) => (
                  <RewardCard key={tier.ids} tier={tier} row2Index={row2Index} />
                ))}
              </div>
            </div>

            <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-slate-500 sm:text-sm">
              Visuals are representative. Actual models, colours, and fulfilment may vary by stock and region. For eligibility questions, contact support through your SuccessFlow account or the official channels listed on the About page.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Rewards;
